/** An image field (the tour's cover and meeting-point photo, plan §10.1 B-lift; the catalog
 *  records' category icon, route map and day image, §10.2 / D3): the stored image as a
 *  downsized thumbnail, the picked image in its place until the save, and Upload /
 *  Replace / Remove.
 *
 *  The record kind supplies the stored image (`stored`) and how a picked file waits for
 *  the save (`staging`, the strategy). Each strategy has its own value shape and its own
 *  render branch — the widget switches on the KIND, never on the value's shape:
 *  - `local`: the file stays on the page (`StagedFiles`) and the draft holds its token
 *    `{staged, name, size}`; the save uploads it. A reload keeps the token but loses the
 *    file — the widget says "re-attach".
 *  - `uploaded` (upload first): picking a file uploads it at once (`POST /media/staged/`,
 *    the record kind's `upload`) and the draft holds the returned Image id — a number, as
 *    the changeset sends it; null is no image. The widget shows that image (downsized,
 *    from the CDN URL the upload answered — never a blob URL, and nothing but the id goes
 *    into the draft; the tab remembers the upload beside the draft, across a reload —
 *    `uploadedImages`), with progress while it uploads and the server's words when it is
 *    refused. A save that names an upload the daily sweep removed is refused
 *    (`staged_image_gone`, "Upload it again"): the widget then offers exactly that.
 *
 *  Two shapes (`shape`): `crop` — a 72×48 cover crop, for a photo (a tour's cover, a route
 *  map); `tile` — a 48 px square tile holding the whole image, for an icon (a category's),
 *  on the paper surface so a transparent icon reads as itself. A vector (`.svg`) is never
 *  sent through the resize service: it is shown as stored, in either shape.
 *
 *  Zero re-layout: the row shows the same thumbnail in both modes (`ImageView` is the
 *  view-mode row, in the same shape and with the same empty words). The row never wraps: the
 *  name takes what the thumbnail and the buttons leave, ending in an ellipsis; only a refused
 *  upload's line goes under it.
 *
 *  Remove is an ordinary, undoable draft change — not a danger colour — described by the
 *  field like Upload / Replace; the upload's progress is a polite live region. */
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { isSweptUpload, stagedImageErrorMessage, type StagedImageUpload } from '@/api/stagedImages';
import { useRecordEditor } from '@/features/records/editor/context';
import type { RecordWidgetProps } from '@/features/records/editor/types';
import { IMAGE_PRESET, imageChunksFromUrl, imageUrl } from '@/utils/imageUrl';

import { formatBytes, isStagedFile, type StagedFileRef, type StagedFiles } from './stagedFiles';
import { rememberUpload, uploadedImage } from './uploadedImages';

/** A stored image: its CDN keys (`p` / `s`) when it has them, else its URL. */
export type StoredImage = { id: number; url: string | null; p: string | null; s: string | null; name?: string | null };

/** How a picked image waits for the save. */
export type ImageStaging =
  /** The draft holds a `StagedFiles` token; the save sends the file (the tour's PATCH). */
  | { kind: 'local'; files: StagedFiles }
  /** The file is uploaded on pick; the draft holds the Image id (the catalog changesets). */
  | { kind: 'uploaded'; upload: StagedImageUpload };

/** How the field shows its image: `crop` (a photo) or `tile` (an icon) — see above. */
export type ImageShape = 'crop' | 'tile';

const FRAME_SX = { borderRadius: 1, border: 1, borderColor: 'divider', flex: 'none' } as const;
const THUMB_SX: Readonly<Record<ImageShape, object>> = {
  crop: { ...FRAME_SX, width: 72, height: 48, objectFit: 'cover' },
  tile: { ...FRAME_SX, width: 48, height: 48, objectFit: 'contain', bgcolor: 'background.paper' },
};

/** A vector needs no resize, and the resize service is never asked for one. */
const isVector = (name: string) => /\.svg$/i.test(name);

/** A stored image as a DOWNSIZED thumbnail (a vector as stored); the original opens in a
 *  new tab on click (the project's image rule). */
export function ImageThumb({ image, label, shape = 'crop' }: { image: StoredImage; label: string; shape?: ImageShape }) {
  const ref = image.p && image.s ? { p: image.p, s: image.s } : null;
  const thumb = ref ? (isVector(ref.s) ? imageUrl(ref) : imageUrl(ref, IMAGE_PRESET.THUMB)) : null;
  const original = ref ? imageUrl(ref) : image.url;
  if (!thumb) {
    return original ? (
      <Link href={original} target="_blank" rel="noopener noreferrer" variant="body2">
        Open {label.toLowerCase()}
      </Link>
    ) : (
      <Typography variant="body2" color="text.secondary">
        Image #{image.id}
      </Typography>
    );
  }
  return (
    <Box component="a" href={original ?? undefined} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex' }} aria-label={`Open the ${label.toLowerCase()} in a new tab`}>
      <Box component="img" src={thumb} alt="" loading="lazy" sx={THUMB_SX[shape]} />
    </Box>
  );
}

function StagedThumb({ staged, files, shape }: { staged: StagedFileRef; files: StagedFiles; shape: ImageShape }) {
  const file = files.get(staged);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file || typeof URL.createObjectURL !== 'function') return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url ? (
    <Box component="img" src={url} alt="" sx={THUMB_SX[shape]} data-staged-thumb={shape} />
  ) : (
    <Box sx={{ ...THUMB_SX[shape], bgcolor: 'action.hover' }} data-staged-thumb={shape} />
  );
}

/** The thumbnail, the name, the buttons — one line; a refused upload's line (`flexBasis`
 *  100%) is the only thing that goes under it. */
const ROW_SX = { display: 'flex', alignItems: 'center', gap: 1, minHeight: 48, minWidth: 0, flexWrap: 'wrap' } as const;
/** The name takes the room left, from nothing, and ends in an ellipsis (`noWrap`): it never
 *  pushes the buttons onto a line of their own. */
const NAME_SX = { minWidth: 0, flex: '1 1 0' } as const;
const BUTTONS_SX = { display: 'flex', gap: 0.5, flex: 'none' } as const;

/** The words of a field without an image. */
export const NO_IMAGE = 'No image';

/** The view-mode row of an image field: the stored image downsized, linking the original,
 *  and its name — or `emptyText` — on the edit row's geometry (a record kind's `View`). */
export function ImageView({
  image,
  label,
  shape = 'crop',
  emptyText = NO_IMAGE,
}: {
  image: StoredImage | null;
  label: string;
  shape?: ImageShape;
  emptyText?: string;
}) {
  return (
    <Box sx={ROW_SX} data-image-shape={shape}>
      {image && <ImageThumb image={image} label={label} shape={shape} />}
      <Typography variant="body2" color="text.secondary" noWrap sx={NAME_SX}>
        {image ? (image.name ?? '') : emptyText}
      </Typography>
    </Box>
  );
}

export interface ImageWidgetProps extends RecordWidgetProps {
  /** The field's stored image (null: none). */
  stored: StoredImage | null;
  staging: ImageStaging;
  /** Default `crop`; an icon field is a `tile` — as its view-mode row (`ImageView`). */
  shape?: ImageShape;
  /** The words without an image (default "No image") — as its view-mode row. */
  emptyText?: string;
}

export function ImageWidget({ stored, staging, shape = 'crop', emptyText = NO_IMAGE, ...p }: ImageWidgetProps) {
  return staging.kind === 'uploaded' ? (
    <UploadedImageInput {...p} stored={stored} upload={staging.upload} shape={shape} emptyText={emptyText} />
  ) : (
    <LocalImageInput {...p} stored={stored} files={staging.files} shape={shape} emptyText={emptyText} />
  );
}

// ---------------------------------------------------------------------------
// local: the draft holds a StagedFiles token, the save sends the file
// ---------------------------------------------------------------------------

function LocalImageInput({
  stored,
  files,
  shape,
  emptyText,
  ...p
}: RecordWidgetProps & { stored: StoredImage | null; files: StagedFiles; shape: ImageShape; emptyText: string }) {
  const input = useRef<HTMLInputElement>(null);
  const v = p.value;
  const staged = isStagedFile(v) ? v : null;
  const missing = staged !== null && !files.has(staged);
  const showsStored = !staged && v !== null && v !== undefined && stored !== null;

  return (
    <Box sx={ROW_SX} data-image-shape={shape}>
      {staged ? (
        <StagedThumb staged={staged} files={files} shape={shape} />
      ) : showsStored ? (
        <ImageThumb image={stored} label={p.field.label} shape={shape} />
      ) : null}
      <Typography variant="body2" color={missing ? 'error' : 'text.secondary'} noWrap sx={NAME_SX}>
        {staged
          ? `${staged.name} · ${formatBytes(staged.size)}${missing ? ' · re-attach' : ' · uploads on save'}`
          : showsStored
            ? (stored.name ?? '')
            : emptyText}
      </Typography>
      <Box sx={BUTTONS_SX}>
        <Button size="small" onClick={() => input.current?.click()} disabled={p.disabled} aria-describedby={p.describedBy}>
          {staged || showsStored ? 'Replace…' : 'Upload…'}
        </Button>
        {(staged || showsStored) && (
          <Button size="small" onClick={() => p.onChange(null)} disabled={p.disabled} aria-describedby={p.describedBy}>
            Remove
          </Button>
        )}
      </Box>
      <input
        ref={input}
        id={p.id}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) p.onChange(files.stage(f), { label: `${p.field.label}: ${f.name}` });
          e.target.value = '';
        }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// uploaded: picking uploads at once, the draft holds the Image id
// ---------------------------------------------------------------------------

function UploadedImageInput({
  stored,
  upload,
  shape,
  emptyText,
  ...p
}: RecordWidgetProps & { stored: StoredImage | null; upload: StagedImageUpload; shape: ImageShape; emptyText: string }) {
  const { conflictsFor, pathOf, rememberOptions } = useRecordEditor();
  const input = useRef<HTMLInputElement>(null);
  const pending = useRef<AbortController | null>(null);
  const [busy, setBusy] = useState<{ name: string; progress: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Leaving edit mode (or the page) mid-upload drops it: nothing lands in the draft after.
  useEffect(() => () => pending.current?.abort(), []);

  const id = typeof p.value === 'number' ? p.value : null;
  const isStored = id !== null && stored !== null && id === stored.id;
  // This tab's upload — kept across a reload with the draft (`uploadedImages`).
  const uploaded = id !== null && !isStored ? uploadedImage(id) : undefined;
  const swept = id !== null && conflictsFor(p.rec, pathOf(p.field)).some(isSweptUpload);
  const label = p.field.label;

  const pick = async (file: File) => {
    pending.current?.abort();
    const ctrl = new AbortController();
    pending.current = ctrl;
    setError(null);
    setBusy({ name: file.name, progress: 0 });
    try {
      const answer = await upload(file, {
        signal: ctrl.signal,
        onProgress: (progress) => setBusy((b) => (b && pending.current === ctrl ? { ...b, progress } : b)),
      });
      if (ctrl.signal.aborted) return;
      const chunks = imageChunksFromUrl(answer.url);
      rememberUpload(answer.image_id, {
        image: { id: answer.image_id, url: answer.url, p: chunks?.p ?? null, s: chunks?.s ?? null, name: file.name },
        size: file.size,
      });
      // The ledger and the "was …" caption name it by its file.
      rememberOptions(p.field.key, [{ id: answer.image_id, label: file.name }]);
      p.onChange(answer.image_id, { label: `${label}: ${file.name}` });
    } catch (e) {
      if (!ctrl.signal.aborted) setError(stagedImageErrorMessage(e));
    } finally {
      if (pending.current === ctrl) {
        pending.current = null;
        setBusy(null);
      }
    }
  };

  const cancel = () => {
    pending.current?.abort();
    pending.current = null;
    setBusy(null);
  };

  const pct = busy?.progress == null ? null : Math.round(busy.progress * 100);
  const placeholder = (icon?: boolean) => (
    <Box sx={{ ...THUMB_SX[shape], bgcolor: 'action.hover', display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
      {icon && <BrokenImageOutlinedIcon aria-hidden sx={{ fontSize: 20 }} />}
    </Box>
  );

  let thumb: ReactNode = null;
  let text: string;
  if (busy) {
    thumb = (
      <Box sx={{ ...THUMB_SX[shape], bgcolor: 'action.hover', display: 'grid', placeItems: 'center' }}>
        <CircularProgress
          size={20}
          variant={pct === null ? 'indeterminate' : 'determinate'}
          {...(pct === null ? {} : { value: pct })}
          aria-label={`Uploading ${busy.name}`}
        />
      </Box>
    );
    text = `Uploading ${busy.name}${pct === null ? '…' : ` · ${pct}%`}`;
  } else if (id === null) {
    text = emptyText;
  } else if (swept) {
    thumb = placeholder(true);
    text = `${uploaded?.image.name ?? `Image #${id}`} · no longer available`;
  } else if (isStored) {
    thumb = <ImageThumb image={stored} label={label} shape={shape} />;
    text = stored.name ?? '';
  } else if (uploaded) {
    thumb = <ImageThumb image={uploaded.image} label={label} shape={shape} />;
    text = `${uploaded.image.name ?? ''} · ${formatBytes(uploaded.size)} · uploaded`;
  } else {
    // An upload whose name and URL this tab no longer holds (storage blocked or cleared).
    thumb = placeholder();
    text = `Uploaded image #${id}`;
  }

  return (
    <Box sx={ROW_SX} data-image-shape={shape}>
      {thumb}
      {/* Polite: the upload's start, progress and end are said, never interrupting. */}
      <Typography variant="body2" color="text.secondary" noWrap sx={NAME_SX} aria-live="polite" data-image-name>
        {text}
      </Typography>
      <Box sx={BUTTONS_SX}>
        {busy ? (
          <Button size="small" onClick={cancel}>
            Cancel upload
          </Button>
        ) : (
          <>
            <Button size="small" onClick={() => input.current?.click()} disabled={p.disabled} aria-describedby={p.describedBy}>
              {swept ? 'Upload it again…' : id !== null ? 'Replace…' : 'Upload…'}
            </Button>
            {id !== null && (
              <Button
                size="small"
                onClick={() => {
                  setError(null);
                  p.onChange(null);
                }}
                disabled={p.disabled}
                aria-describedby={p.describedBy}
              >
                Remove
              </Button>
            )}
          </>
        )}
      </Box>
      {error && (
        <Box role="alert" sx={{ flexBasis: '100%', display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0, color: 'error.main' }}>
          <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16, mt: '2px', flex: 'none' }} />
          <Typography variant="caption" color="error">
            Not uploaded: {error}
          </Typography>
        </Box>
      )}
      <input
        ref={input}
        id={p.id}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void pick(f);
        }}
      />
    </Box>
  );
}
